import React, { useState, useEffect, useMemo } from 'react';
import { userService } from '../services';
import { validators } from '../utils/validators';
import { normalizeRole, formatRoleLabel } from '../utils/roles';
import './UserManagement.css';

const SECONDARY_TRADES = ['NIT', 'SOD', 'ETEL', 'CSA'];

/**
 * UserManagement Component
 * Demonstrates all CRUD operations using the userService
 */
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const emptyFormState = () => ({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    role: 'reception',
    tracks: ['secondary'],
    assignedTrades: [],
  });

  // Form state - matches backend User model: fullName, username, email, phone, password, role
  const [formData, setFormData] = useState(emptyFormState());

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!showForm) return undefined;
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setFormData(emptyFormState());
        setSelectedUser(null);
        setShowForm(false);
        setError('');
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showForm]);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (err) {
      setError(err.error || err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const validateUserForm = (isCreate) => {
    let msg = validators.required(formData.fullName?.trim(), 'Full name');
    if (msg) return msg;
    msg = validators.required(formData.username?.trim(), 'Username');
    if (msg) return msg;
    msg = validators.email(formData.email);
    if (msg) return msg;
    msg = validators.phone(formData.phone);
    if (msg) return msg;
    if (isCreate) {
      msg = validators.required(formData.password, 'Password');
      if (msg) return msg;
      msg = validators.minLength(formData.password, 6, 'Password');
      if (msg) return msg;
    } else if (formData.password && formData.password.length > 0) {
      msg = validators.minLength(formData.password, 6, 'Password');
      if (msg) return msg;
    }
    const existing = users.find((u) => u.username?.toLowerCase() === formData.username?.trim().toLowerCase());
    if (existing && existing._id !== selectedUser?._id) return 'Username is already in use.';
    if (normalizeRole(formData.role) === 'instructor' && formData.tracks.includes('secondary')) {
      if (!Array.isArray(formData.assignedTrades) || formData.assignedTrades.length === 0) {
        return 'Assign at least one trade for instructors.';
      }
    }
    return null;
  };

  // Create new user
  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    const validationError = validateUserForm(true);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await userService.createUser(formData);
      setSuccessMessage('User created successfully!');
      setShowForm(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      setError(err.error || err.message || 'Failed to create user');
    }
  };

  // Update user
  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!selectedUser) return;
    const validationError = validateUserForm(false);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      // Don't send password if it's empty
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      await userService.updateUser(selectedUser._id, updateData);
      setSuccessMessage('User updated successfully!');
      setShowForm(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (err) {
      setError(err.error || err.message || 'Failed to update user');
    }
  };

  // Delete user
  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    setError('');
    setSuccessMessage('');

    try {
      await userService.deleteUser(userId);
      setSuccessMessage('User deleted successfully!');
      fetchUsers();
    } catch (err) {
      setError(err.error || err.message || 'Failed to delete user');
    }
  };

  // Edit user (populate form)
  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      fullName: user.fullName || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '', // Don't populate password
      role: normalizeRole(user.role),
      tracks: Array.isArray(user.tracks)
        ? user.tracks
        : [String(user.track || 'secondary').toLowerCase()],
      assignedTrades: Array.isArray(user.assignedTrades) ? user.assignedTrades : [],
    });
    setShowForm(true);
  };

  const openAddUserForm = () => {
    setSelectedUser(null);
    setFormData(emptyFormState());
    setShowForm(true);
    setError('');
  };

  // Reset form
  const resetForm = () => {
    setFormData(emptyFormState());
    setSelectedUser(null);
    setShowForm(false);
    setError('');
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTradeToggle = (trade) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.assignedTrades) ? prev.assignedTrades : [];
      const next = current.includes(trade)
        ? current.filter((t) => t !== trade)
        : [...current, trade];
      return { ...prev, assignedTrades: next };
    });
  };

  const handleTrackToggle = (track) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.tracks) ? prev.tracks : [];
      const next = current.includes(track)
        ? current.filter((t) => t !== track)
        : [...current, track];
      // Keep at least one selected
      return { ...prev, tracks: next.length > 0 ? next : [track] };
    });
  };

  const filteredUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.fullName, u.username, u.email, u.phone, normalizeRole(u.role)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [users, searchTerm]);

  return (
    <div className="user-management">
      <div className="header">
        <div className="header-left">
          <h1>User management</h1>
          <p className="header-desc">Add staff accounts and control platform access by role.</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="actions">
        <button type="button" className="btn-primary" onClick={openAddUserForm}>
          + Create user
        </button>
        <button type="button" className="btn-secondary" onClick={fetchUsers}>
          Refresh
        </button>
      </div>

      <div className="users-table">
        <div className="users-table-head">
          <h2>Users List</h2>
          <div className="users-search">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, username, email, phone or role..."
            />
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <p className="users-empty">No users found.</p>
        ) : (
          <div className="users-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="user-cell">
                        <span className="user-avatar">
                          {(user.fullName || user.username || 'U').charAt(0).toUpperCase()}
                        </span>
                        <span>{user.fullName || '—'}</span>
                      </div>
                    </td>
                    <td>{user.username}</td>
                    <td>{user.email || '—'}</td>
                    <td>{user.phone || '—'}</td>
                    <td>
                      <span className={`role-badge role-${normalizeRole(user.role)}`}>
                        {formatRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button className="btn-edit" onClick={() => handleEdit(user)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(user._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="user-modal-overlay" onClick={resetForm}>
          <div className="user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="user-modal-head">
              <h2>{selectedUser ? 'Edit User' : 'Create New User'}</h2>
              <button type="button" className="user-modal-close" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={selectedUser ? handleUpdate : handleCreate}>
              <div className="user-form-grid">
                <div className="form-group">
                  <label htmlFor="fullName">Full Name *</label>
                  <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} required />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password {selectedUser && '(leave empty to keep current)'}</label>
                  <input type="password" id="password" name="password" value={formData.password} onChange={handleInputChange} required={!selectedUser} />
                </div>
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select id="role" name="role" value={formData.role} onChange={handleInputChange}>
                    <option value="reception">Reception</option>
                    <option value="finance">Finance</option>
                    <option value="instructor">Instructor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                {normalizeRole(formData.role) === 'instructor' && (
                  <>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Tracks</label>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {['secondary', 'professional'].map((track) => {
                          const active = formData.tracks.includes(track);
                          return (
                            <button
                              key={track}
                              type="button"
                              onClick={() => handleTrackToggle(track)}
                              style={{
                                border: `1px solid ${active ? '#2563eb' : '#d1d5db'}`,
                                background: active ? '#dbeafe' : '#fff',
                                color: active ? '#1e40af' : '#374151',
                                borderRadius: 999,
                                padding: '6px 12px',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                              }}
                            >
                              {track}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {formData.tracks.includes('secondary') && (
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Assigned trades</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {SECONDARY_TRADES.map((trade) => {
                            const active = formData.assignedTrades.includes(trade);
                            return (
                              <button
                                key={trade}
                                type="button"
                                onClick={() => handleTradeToggle(trade)}
                                style={{
                                  border: `1px solid ${active ? '#2563eb' : '#d1d5db'}`,
                                  background: active ? '#dbeafe' : '#fff',
                                  color: active ? '#1e40af' : '#374151',
                                  borderRadius: 999,
                                  padding: '6px 12px',
                                  fontSize: 12,
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                {trade}
                              </button>
                            );
                          })}
                        </div>
                        <small style={{ color: '#6b7280' }}>
                          Instructors can teach one or more trades in the current cohort.
                        </small>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {selectedUser ? 'Save changes' : 'Create user'}
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
